import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { BASE_URL } from '../Api';
import { Helmet, HelmetProvider } from "react-helmet-async";
import FreeHighlight from '../partials/FreeHighlight';


const StockControlPlugin = () => {
  const { t } = useTranslation('stockcontrol');

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>Stock control - Aronium</title>
          <meta name="description" content="Don't lose track of your stock and stay informed of low stock levels" />
        </Helmet>
      </HelmetProvider>

      <section className="py-8 max-w-screen-xl px-4 mx-auto">
        <div className="max-w-screen-xl mx-auto text-center">
          <h1 className="text-4xl mb-4">
            {t('title')}
          </h1>
          <p>
            {t('description')}
          </p>

          {/* Call to Action Buttons */}
          <div className="mt-8 sm:mt-12 flex flex-col sm:flex-row justify-center gap-4 sm:gap-6">
            <a
              href={`${BASE_URL}/plugins/stock-control/latest/Aronium.Pos.StockControl.Lite.exe`}
              className="inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-semibold rounded-full text-gray-900 bg-gradient-to-r from-[#A5D6A7] to-[#81C784] hover:bg-gradient-to-l transition duration-300 shadow-lg focus:ring-4 focus:ring-green-300 focus:outline-none hover:no-underline"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3"
                />
              </svg>
              {t('heroButtonLite')}
            </a>
            <a
              href={`${BASE_URL}/plugins/stock-control/latest/Aronium.Pos.StockControl.Pro.exe`}
              className="inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-semibold rounded-full text-gray-900 bg-gradient-to-r from-[#64B5F6] to-[#42A5F5] hover:bg-gradient-to-l transition duration-300 shadow-lg focus:ring-4 focus:ring-blue-300 focus:outline-none hover:no-underline"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3"
                />
              </svg>
              {t('heroButtonPro')}
            </a>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <div className="flex flex-col text-center mt-10 px-4">
        <div className="grid place-content-center justify-items-center">
          <img src="/images/stock-control.png" className='border-t-8 border-l border-r rounded-t-xl border-solid border-stone-500 xl:max-w-6xl' alt="Expiration date screen" />
        </div>
      </div>
      <div className="bg-gray-100 py-20">
        <div className="relative max-w-7xl mx-auto text-center px-8 lg:px-12">
          <h2 className="text-5xl text-gray-800 leading-tight mb-8">
            <Trans ns='stockcontrol' i18nKey="overviewTitle" components={{ strong: <span className='font-semibold' /> }} />
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            <Trans ns='stockcontrol' i18nKey="overviewDescription" components={{ strong: <span className='font-semibold' /> }} />
          </p>
        </div>
        <FreeHighlight className="mt-12" />
      </div>

      {/* Key Features Section */}
      <div className="py-24">
        <div className="max-w-6xl mx-auto px-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
          <div className="bg-white p-8 shadow-lg rounded-lg border border-gray-200">
            <h3 className="text-2xl font-semibold text-gray-800">{t('feature1Title')}  </h3>
            <p className="mt-4 text-gray-600">
              {t('feature1Description')}
            </p>
          </div>
          <div className="bg-white p-8 shadow-lg rounded-lg border border-gray-200">
            <h3 className="text-2xl font-semibold text-gray-800">{t('feature2Title')} </h3>
            <p className="mt-4 text-gray-600">
              {t('feature2Description')}
            </p>
          </div>
        </div>
      </div>

      {/* Call to Action Section */}
      <div className="bg-gradient-to-tr from-stone-400 to-stone-700 py-24 text-center text-white">
        <div className="relative max-w-6xl mx-auto px-4">
          <h2 className="text-4xl xl:text-6xl mb-6">{t('callToActionTitle')}  </h2>
          <p className="text-xl mb-10">
            <p className="text-xl mb-10">
              <Trans ns='stockcontrol' i18nKey="callToActionDescription" components={{ strong: <span className='font-semibold' /> }} />
            </p>
          </p>
          <a
            href="https://help.aronium.com/hc/en-us/articles/115002983669-Stock-control"
            className="button-rounded-white px-12 py-4"
            target="_blank"
            rel="noreferrer">
            {t('learnMore')} <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="ml-2 w-6 h-6 transition-all">
              <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 8.25 21 12m0 0-3.75 3.75M21 12H3" />
            </svg>
          </a>
        </div>
      </div>
    </>
  );
};

export default StockControlPlugin;
